import { faPaperclip, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import './EmailComposer.css';
import { Color } from '@/constants/color';
import { EMAIL_MAX_LENGTH } from '@/constants/messageLimits';
import AppContext from '@/contexts/appContext';
import { useDiscardConfirm } from '@/hooks/useDiscardConfirm';
import { Message } from '@/types/message';
import Button from '@/ui/components/Button';
import InputField from '@/ui/components/Form/InputField';
import RichTextEditor from '@/ui/components/Form/RichTextEditor';
import { formatBytes } from '@/utils/formatBytes';
import { htmlToMarkdown } from '@/utils/htmlToMarkdown';
import { uploadToPresignedUrl } from '@/utils/uploadToPresignedUrl';

const EMPTY_RICH_TEXT = '<p><br/></p>';

const ALLOWED_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
]);

const ACCEPT_ATTR = '.jpg,.jpeg,.png,.gif,.webp,.bmp,.pdf,.docx,.xlsx';

const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB per file, enforced again server-side
const MAX_TOTAL_BYTES = 6 * 1024 * 1024; // 6 MB total, enforced again server-side (SES raw message cap)

type AttachmentState = {
  key: string;
  name: string;
  size: number;
  file: File;
  attachmentId?: string;
  uploading: boolean;
  error?: string;
  retryable?: boolean;
};

interface Props {
  recipientEmail: string;
  subject: string;
  body: string;
  isLoading: boolean;
  replyToMessage: Message | null;
  disableAttachments?: boolean;
  onSubjectChange: (value: string) => void;
  onBodyChange: (value: string) => void;
  onSend: (
    draftId: string | null,
    attachments: { id: string; name: string; size: number }[]
  ) => void;
  onDiscard: () => void;
  onDismissReply: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
  onPrepareAttachment?: (
    file: File,
    draftId: string | null
  ) => Promise<{
    draft_id: string;
    attachment_id: string;
    upload_url: string;
    name: string;
    size: number;
  }>;
  onRemoveAttachment?: (draftId: string, attachmentId: string) => Promise<void>;
}

const EmailComposer = ({
  recipientEmail,
  subject,
  body,
  isLoading,
  replyToMessage,
  disableAttachments = false,
  onSubjectChange,
  onBodyChange,
  onSend,
  onDiscard,
  onDismissReply,
  onDirtyChange,
  onPrepareAttachment,
  onRemoveAttachment
}: Props) => {
  const [attachments, setAttachments] = useState<AttachmentState[]>([]);
  const [draftId, setDraftId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { confirmDiscard } = useDiscardConfirm('You have an unsaved message. Discard it?');
  const { addMessage } = useContext(AppContext);

  const hasContent = Boolean(
    (body && body !== EMPTY_RICH_TEXT) || subject || attachments.length > 0
  );

  useEffect(() => {
    onDirtyChange?.(hasContent);
  }, [hasContent, onDirtyChange]);

  const handleSubjectChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onSubjectChange(event?.target?.value ?? '');
    },
    [onSubjectChange]
  );

  const totalBytes = attachments.reduce((sum, a) => sum + a.size, 0);
  const isOverTotalLimit = totalBytes > MAX_TOTAL_BYTES;
  const isOverBodyLimit = htmlToMarkdown(body).length > EMAIL_MAX_LENGTH;
  const hasUploadInProgress = attachments.some((a) => a.uploading);
  const hasAttachmentErrors = attachments.some((a) => a.error);

  const isSendDisabled =
    (!replyToMessage && !subject) ||
    !body ||
    body === EMPTY_RICH_TEXT ||
    isOverTotalLimit ||
    isOverBodyLimit ||
    hasUploadInProgress ||
    hasAttachmentErrors;

  const updateAttachment = (key: string, patch: Partial<AttachmentState>) =>
    setAttachments((prev) => prev.map((a) => (a.key === key ? { ...a, ...patch } : a)));

  const uploadFile = async (
    key: string,
    file: File,
    currentDraftId: string | null
  ): Promise<string | null> => {
    if (!onPrepareAttachment) return currentDraftId;
    let prepareResult: Awaited<ReturnType<typeof onPrepareAttachment>> | null = null;
    try {
      prepareResult = await onPrepareAttachment(file, currentDraftId);
      if (!currentDraftId) {
        setDraftId(prepareResult.draft_id);
      }
      await uploadToPresignedUrl(prepareResult.upload_url, file);
      updateAttachment(key, {
        attachmentId: prepareResult.attachment_id,
        uploading: false,
        error: undefined,
        retryable: undefined
      });
      return prepareResult.draft_id;
    } catch {
      updateAttachment(key, {
        attachmentId: prepareResult?.attachment_id,
        uploading: false,
        error: 'Upload failed',
        retryable: true
      });
      return currentDraftId;
    }
  };

  const handleFiles = async (files: File[]) => {
    let currentDraftId = draftId;
    for (const file of files) {
      const key = `${file.name}-${Date.now()}`;
      if (!ALLOWED_MIME_TYPES.has(file.type)) {
        setAttachments((prev) => [
          ...prev,
          {
            key,
            file,
            name: file.name,
            size: file.size,
            uploading: false,
            error: 'File type not allowed'
          }
        ]);
        continue;
      }
      if (file.size > MAX_FILE_BYTES) {
        setAttachments((prev) => [
          ...prev,
          {
            key,
            file,
            name: file.name,
            size: file.size,
            uploading: false,
            error: 'Exceeds 5 MB limit'
          }
        ]);
        continue;
      }
      setAttachments((prev) => [
        ...prev,
        { key, file, name: file.name, size: file.size, uploading: true }
      ]);
      currentDraftId = await uploadFile(key, file, currentDraftId);
    }
  };

  const retryUpload = async (key: string) => {
    const attachment = attachments.find((a) => a.key === key);
    if (!attachment) return;

    updateAttachment(key, { uploading: true, error: undefined, attachmentId: undefined });

    if (attachment.attachmentId && draftId && onRemoveAttachment) {
      await onRemoveAttachment(draftId, attachment.attachmentId).catch(() => {});
    }

    await uploadFile(key, attachment.file, draftId);
  };

  const removeAttachment = async (key: string) => {
    const attachment = attachments.find((a) => a.key === key);
    if (attachment?.attachmentId && draftId && onRemoveAttachment) {
      try {
        await onRemoveAttachment(draftId, attachment.attachmentId);
      } catch {
        updateAttachment(key, { error: 'Failed to remove attachment' });
        return;
      }
    }
    setAttachments((prev) => prev.filter((a) => a.key !== key));
  };

  const handleDiscard = () => {
    if (hasContent && !confirmDiscard()) return;
    setAttachments([]);
    setDraftId(null);
    onDiscard();
  };

  return (
    <div className="flex flex-col gap-3">
      {replyToMessage && (
        <div className="flex items-center justify-between px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-sm">
          <span className="text-blue-700 truncate">
            <span className="font-medium">Replying to:</span>{' '}
            <span className="text-blue-600">
              {replyToMessage.subject ?? replyToMessage.preview}
            </span>
          </span>
          <button
            type="button"
            aria-label="Dismiss reply"
            onClick={onDismissReply}
            className="ml-2 flex-shrink-0 text-blue-500 hover:text-blue-700 p-1 rounded hover:bg-blue-100 transition-colors">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      )}
      <div className="text-sm">
        <p className="text-gray-600">
          To: <span className="mp-sensitive font-medium text-gray-800">{recipientEmail}</span>
        </p>
      </div>

      {!replyToMessage && (
        <InputField
          id="email-subject"
          type="text"
          label="Subject"
          placeholder=""
          value={subject}
          wrapperClassNames="w-full"
          onChange={handleSubjectChange}
        />
      )}

      <div className="email-composer-editor">
        <RichTextEditor
          value={body}
          onChange={onBodyChange}
          showEmojis
          showHeaders={false}
          showUnderline={false}
          onImagePaste={() =>
            addMessage(
              'warning',
              'Currently, we do not support pasting images into the message body. Use Attach to add them instead.'
            )
          }
        />
      </div>

      {/* Attachment chips */}
      {!disableAttachments && attachments.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {attachments.map((a) => (
            <div
              key={a.key}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border ${
                a.error
                  ? 'border-red-300 bg-red-50 text-red-700'
                  : 'border-gray-200 bg-gray-50 text-gray-700'
              }`}>
              {a.uploading && (
                <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
              )}
              <span className="max-w-40 truncate">{a.name}</span>
              {!a.uploading && !a.error && (
                <span className="text-gray-400">({formatBytes(a.size)})</span>
              )}
              {a.error && <span>— {a.error}</span>}
              {a.retryable && (
                <button
                  type="button"
                  aria-label={`Retry upload for ${a.name}`}
                  onClick={() => void retryUpload(a.key)}
                  className="ml-0.5 underline hover:opacity-70 transition-opacity">
                  Try again
                </button>
              )}
              <button
                type="button"
                aria-label={`Remove ${a.name}`}
                onClick={() => void removeAttachment(a.key)}
                className="ml-0.5 hover:opacity-70 transition-opacity">
                <FontAwesomeIcon icon={faXmark} className="text-xs" />
              </button>
            </div>
          ))}
        </div>
      )}

      {!disableAttachments && isOverTotalLimit && (
        <p className="text-xs text-red-600">
          Total attachments exceed 6 MB. Remove some files before sending.
        </p>
      )}

      {isOverBodyLimit && (
        <p className="text-xs text-red-600">
          Message exceeds the {EMAIL_MAX_LENGTH.toLocaleString()} character limit and cannot be
          sent.
        </p>
      )}

      {/* Hidden file input */}
      {!disableAttachments && (
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT_ATTR}
          multiple
          className="hidden"
          onChange={(e) => {
            void handleFiles(Array.from(e.target.files ?? []));
            e.target.value = '';
          }}
        />
      )}

      <div className="flex items-center justify-between gap-2">
        {!disableAttachments && (
          <button
            type="button"
            aria-label="Attach files"
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100 transition-colors">
            <FontAwesomeIcon icon={faPaperclip} />
            Attach
          </button>
        )}
        <div className={`flex items-center gap-2 ${disableAttachments ? 'ml-auto' : ''}`}>
          <Button
            type="button"
            disabled={isSendDisabled}
            loading={isLoading}
            onClick={() => {
              const successfulAttachments = attachments
                .filter((a) => !a.uploading && !a.error && a.attachmentId)
                .map((a) => ({ id: a.attachmentId!, name: a.name, size: a.size }));
              onSend(draftId, successfulAttachments);
            }}>
            Send
          </Button>
          <Button type="button" color={Color.WHITE} onClick={handleDiscard}>
            Discard
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EmailComposer;
